import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

// Container that places 3 cards in a row (or 1 column on mobile)
const EducationLinksContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '20px',
  maxWidth: '1200px',
  margin: '40px auto', // space above/below
  padding: '0 20px',   // optional horizontal padding
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    margin: '12px auto 28px',
  },
}));

// Each card with simple clean styling matching PopularLinks
const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, rgba(41, 71, 179, 0.3) 0%, rgba(30, 49, 120, 0.2) 100%)',
  border: '2px solid rgba(255, 255, 255, 0.4)',
  padding: '20px',
  borderRadius: '8px',
  color: '#FFFFFF',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: 'rgba(41, 71, 179, 0.4)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  },
}));

// The column title text - clean white and bold
const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontWeight: 'bold',
  fontSize: '1.4rem',
  marginBottom: '16px',
  textAlign: 'center',
  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  letterSpacing: '0.5px',
  '&:hover': {
    color: '#FFFFFF',
    textShadow: '0 0 15px rgba(255, 255, 255, 0.5)',
    transform: 'scale(1.05)',
  },
}));

// Holds each link & its description
const LinkContainer = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
}));

// Company/Link name in neon yellow
const SimpleLink = styled(motion.a)(({ theme }) => ({
  color: '#FFFF00', // Neon yellow
  textDecoration: 'none',
  fontWeight: 'bold',
  display: 'block',
  marginBottom: '6px',
  fontSize: '1.1rem',
  textShadow: '0 0 5px rgba(255, 255, 0, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  letterSpacing: '0.3px',
  '&:hover': {
    transform: 'scale(1.05)',
    color: '#FFFF33',
    textShadow: '0 0 8px rgba(255, 255, 0, 0.8)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

// Row with description & arrow
const DescriptionContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  marginTop: '4px',
  '&:hover p': {
    color: '#FFFFFF',
  },
  '&:hover svg': {
    opacity: 1,
    transform: 'translateX(4px)',
  },
});

// Description text - clean white
const DescriptionText = styled(Typography)(({ theme }) => ({
  margin: 0,
  fontSize: '1rem',
  color: 'rgba(255, 255, 255, 0.8)',
  flex: 1,
  transition: 'color 0.2s ease',
  lineHeight: '1.5',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
}));

// Arrow icon - white to match clean styling
const ArrowIcon = styled(ChevronRight)({
  color: 'rgba(255, 255, 255, 0.7)',
  opacity: 0.5,
  transition: 'all 0.2s ease',
  marginLeft: '5px',
});

const EducationLinks = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Wallets & Exchanges',
      sectionId: 'wallets-exchanges',
      links: [
        { name: 'Gemini Exchange', url: 'https://www.gemini.com', description: 'Leading Exchange & Crypto Institution in US' },
        { name: 'Crypto.com',      url: 'https://www.crypto.com', description: 'Leading Exchange' },
        { name: 'Coinbase',       url: 'https://www.coinbase.com', description: 'Largest Exchange' },
        { name: 'Binance',        url: 'https://www.binance.us', description: 'Leading Exchange & Crypto Institution' },
        { name: 'Metamask',       url: 'https://www.metamask.io', description: 'Leading Defi Wallet' },
      ],
    },
    {
      title: 'Popular Apps',
      sectionId: 'popular-apps',
      links: [
        { name: 'Uniswap',    url: 'https://www.uniswap.org', description: 'Home of decentralized finance & beyond.' },
        { name: 'Rarible',    url: 'https://www.rarible.com', description: 'Leading NFT Marketplace' },
        { name: 'Star Atlas', url: 'https://staratlas.com/',  description: 'Web3 Space Exploration' },
        { name: 'dYdX',       url: 'https://www.dydx.exchange', description: 'Innovative & Emerging Trading' },
        { name: 'Dextools',   url: 'https://www.dextools.io', description: 'Defi trading resources' },
      ],
    },
    {
      title: 'Learning',
      sectionId: 'learning',
      links: [
        { name: 'Defi Lending',       url: 'https://youtu.be/pp9Abjlz7K4?si=RPuLzquvV-dG6va1', description: 'Evolution of Defi Lending' },
        { name: 'Binance Academy',    url: 'https://academy.binance.com',                   description: 'Crypto Finance Education' },
        { name: 'Investopediea',      url: 'https://www.investopedia.com/cryptocurrency-4427699', description: 'Crypto Basic' },
        { name: 'Bitcoin Whitepaper', url: 'https://bitcoin.org/bitcoin.pdf',               description: 'Mechanics & Origin of BTC Protocol' },
        { name: 'Buy Bitcoin',        url: 'https://www.fidelity.com/crypto',               description: 'Easy & Secure bitcoin acquisition' },
      ],
    },
  ];

  // Navigate to InfoCenter's "Resources" tab, optionally with a specific section
  const navigateToResources = (sectionId) => {
    navigate('/info-center', { 
      state: { 
        selectTab: 'Resources',
        section: sectionId 
      }
    });
  };

  return (
    <EducationLinksContainer>
      {sections.map((section, index) => (
        <SectionContainer key={index}>
          {/* Clickable column title */}
          <SectionTitle onClick={() => navigateToResources(section.sectionId)}>
            {section.title}
          </SectionTitle>

          {/* Links */}
          {section.links.map((link, idx) => (
            <LinkContainer key={idx}>
              <SimpleLink
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.name}
              </SimpleLink>

              <DescriptionContainer onClick={() => navigateToResources(section.sectionId)}>
                <DescriptionText>{link.description}</DescriptionText>
                <ArrowIcon size={16} />
              </DescriptionContainer>
            </LinkContainer>
          ))}
        </SectionContainer>
      ))}
    </EducationLinksContainer>
  );
};

export default EducationLinks;
